import React, { useState } from "react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { ModelRoutesCard } from "../components/ModelRoutesCard";
import { Logo } from "../components/Logo";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useI18n } from "../i18n";
import { Check } from "lucide-react";

const LANGS = [
  { v: "id", l: "Bahasa Indonesia" }, { v: "en", l: "English" },
  { v: "zh", l: "中文" }, { v: "es", l: "Español" }, { v: "ar", l: "العربية" },
];
const TZ = ["Asia/Jakarta", "Asia/Singapore", "Asia/Tokyo", "Europe/London", "America/New_York"];

export default function Onboarding() {
  const { user, setUser, logout } = useAuth();
  const { t } = useI18n();
  const nav = useNavigate();
  const [name, setName] = useState(user?.name || "");
  const [appLang, setAppLang] = useState("id");
  const [convLang, setConvLang] = useState("id");
  const [tz, setTz] = useState("Asia/Jakarta");
  const [agree, setAgree] = useState(false);
  const [busy, setBusy] = useState(false);
  const [step, setStep] = useState(1);

  if (!user) { nav("/"); return null; }

  if (step === 2) {
    return (
      <div className="flex min-h-screen items-center justify-center mesh-bg p-6">
        <div className="w-full max-w-2xl fade-up" data-testid="onboard-routes-step">
          <div className="mb-8 flex justify-center"><Logo size={44} /></div>
          <div className="aivora-card p-8">
            <p className="text-xs font-semibold uppercase tracking-wider text-[#2F6BFF]">Langkah 2 dari 2</p>
            <h1 className="mt-1 text-2xl font-bold text-slate-900">Pengaturan Agen</h1>
            <p className="mt-1 text-sm text-slate-500">Tentukan model yang dipakai tiap Agen untuk jenis pekerjaannya. Bawaan mengikuti rekomendasi platform; bisa diubah kapan saja di Profil › Pengaturan Agen.</p>
            <ModelRoutesCard />
            <button onClick={() => nav("/personas/new")} className="btn-grad mt-7 w-full rounded-xl py-3.5 text-sm" data-testid="onboard-create-persona">Lanjut: buat asisten AI pertama Anda</button>
          </div>
        </div>
      </div>
    );
  }

  const finish = async () => {
    if (!agree) { toast.error("Mohon setujui syarat & kebijakan privasi"); return; }
    setBusy(true);
    try {
      const r = await api.post("/auth/onboard", {
        name, app_language: appLang, conversation_language: convLang, timezone: tz,
      });
      setUser(r.data);
      toast.success("Preferensi tersimpan");
      setStep(2);
    } catch (e) { toast.error("Gagal menyimpan"); } finally { setBusy(false); }
  };

  return (
    <div className="flex min-h-screen items-center justify-center mesh-bg p-6">
      <div className="w-full max-w-lg fade-up">
        <div className="mb-8 flex justify-center"><Logo size={44} /></div>
        <div className="aivora-card p-8">
          <h1 className="text-2xl font-bold text-slate-900">Mari atur akun Anda</h1>
          <p className="mt-1 text-sm text-slate-500">Beberapa preferensi dasar untuk memulai.</p>

          <div className="mt-6 space-y-5">
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-500">Nama panggilan</label>
              <input className="input-dark" value={name} onChange={(e) => setName(e.target.value)} data-testid="onboard-name" placeholder="Nama Anda" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-500">Bahasa Aplikasi</label>
                <select className="input-dark" value={appLang} onChange={(e) => setAppLang(e.target.value)} data-testid="onboard-applang">
                  {LANGS.map((l) => <option key={l.v} value={l.v}>{l.l}</option>)}
                </select>
              </div>
              <div>
                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-500">Bahasa Percakapan</label>
                <select className="input-dark" value={convLang} onChange={(e) => setConvLang(e.target.value)} data-testid="onboard-convlang">
                  {LANGS.map((l) => <option key={l.v} value={l.v}>{l.l}</option>)}
                </select>
              </div>
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-500">Zona Waktu</label>
              <select className="input-dark" value={tz} onChange={(e) => setTz(e.target.value)} data-testid="onboard-tz">
                {TZ.map((z) => <option key={z} value={z}>{z}</option>)}
              </select>
            </div>
            <button onClick={() => setAgree(!agree)} data-testid="onboard-agree"
              className="flex w-full items-center gap-3 rounded-xl bg-slate-50 px-4 py-3 text-left text-sm text-slate-600">
              <span className={`flex h-5 w-5 items-center justify-center rounded-md border ${agree ? "btn-grad border-transparent" : "border-slate-300"}`}>
                {agree && <Check size={14} />}
              </span>
              Saya menyetujui <a href="/terms" target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="font-semibold text-[#2F6BFF] hover:underline" data-testid="onboard-terms-link">Syarat Layanan</a> dan <a href="/privacy" target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="font-semibold text-[#2F6BFF] hover:underline" data-testid="onboard-privacy-link">Kebijakan Privasi</a>.
            </button>
          </div>

          <button onClick={finish} disabled={busy} className="btn-grad mt-7 w-full rounded-xl py-3.5 text-sm" data-testid="onboard-finish">
            {busy ? "..." : "Mulai menggunakan Oryntix"}
          </button>
          <button onClick={async () => { await logout(); nav("/"); }} className="mt-3 w-full text-center text-xs text-slate-500">Keluar</button>
        </div>
      </div>
    </div>
  );
}
